// ============================================================
// Raya pricing — single source of truth
// Mirrors the Raya CRM and Raya ERP price sheets (September 2026).
// Every rate below is Rs. per user, per month, exclusive of GST.
// ============================================================

import { PLAY_STORE_URL, APP_STORE_URL } from '../../constants/constants';

export const PRICE_SHEET_DATE = 'September 2026';
export const CURRENCY = '₹';

// ─── Rate card ──────────────────────────────────────────────────────────────
// The two sheets describe one catalogue from opposite ends:
//   CRM sheet — Raya CRM 299 base, Raya ERP Standard as a +300 add-on.
//   ERP sheet — Raya ERP Standard 499 base, CRM & Marketing as a +100 add-on.
// Both land on 599 for CRM + ERP, so a single rate card serves both journeys.
export const RATES = {
  crmOnly: 299,
  erpOnly: 499,
  crmPlusErp: 599,
  pm: 100,
  hr: 150,
  prod: 250,
  everything: 899, // all five modules — 18% off the 1,099 a-la-carte rate
  ai: 499,         // per AI-enabled user, added only to the seats that need it
};

export const YEARLY_MONTHS_CHARGED = 10; // pay for the year, get 2 months free
export const BULK_THRESHOLD = 30;        // above this we quote a bulk discount

// ─── Catalogue ──────────────────────────────────────────────────────────────
// `base` is the rate when the module is the starting point; `addOn` is the rate
// when it joins an existing plan. The two differ only for CRM and ERP Standard.
export const MODULES = [
  {
    key: 'crm',
    name: 'Raya CRM',
    tagline: 'Leads, campaigns, follow-ups and quotes',
    icon: 'contact-round',
    color: '#4f46e5',
    tint: '#eef2ff',
    kind: 'base',
    base: RATES.crmOnly,
    addOn: RATES.crmPlusErp - RATES.erpOnly, // 100 on top of ERP Standard
    includes: ['Lead capture and pipeline', 'Campaigns and follow-ups', 'Quotations', 'Contact and account history'],
  },
  {
    key: 'erp',
    name: 'Raya ERP Standard',
    tagline: 'Inventory, finance, sales and invoicing, purchase, reports',
    icon: 'package',
    color: '#2563eb',
    tint: '#eff6ff',
    kind: 'base',
    base: RATES.erpOnly,
    addOn: RATES.crmPlusErp - RATES.crmOnly, // 300 on top of Raya CRM
    note: 'Sold whole, not in parts.',
    includes: ['Multi-warehouse inventory', 'Sales and invoicing with IRN + QR', 'Purchase, PO and GRN', 'Finance and ledgers', 'Reports and GSTR-ready exports'],
  },
  {
    key: 'pm',
    name: 'Project Management',
    tagline: 'Projects, tasks, time and cost',
    icon: 'clipboard-list',
    color: '#0891b2',
    tint: '#ecfeff',
    kind: 'addon',
    base: RATES.pm,
    addOn: RATES.pm,
    includes: ['Projects and milestones', 'Task boards and assignments', 'Timesheets', 'Cost against budget'],
  },
  {
    key: 'hr',
    name: 'HR & Payroll',
    tagline: 'Attendance, leave, shifts, salary runs',
    icon: 'users',
    color: '#e11d48',
    tint: '#fff1f2',
    kind: 'addon',
    base: RATES.hr,
    addOn: RATES.hr,
    includes: ['Attendance and leave', 'Shift rosters', 'Salary runs and payslips', 'Statutory registers'],
  },
  {
    key: 'prod',
    name: 'Production',
    tagline: 'Manufacturing: BOM, work orders, QC',
    icon: 'factory',
    color: '#d97706',
    tint: '#fffbeb',
    kind: 'addon',
    base: RATES.prod,
    addOn: RATES.prod,
    requires: 'erp',
    note: 'Needs Raya ERP Standard.',
    includes: ['Bills of material', 'Work orders and routing', 'Material issue and stage tracking', 'Quality control'],
  },
];

export const MODULE_KEYS = MODULES.map((m) => m.key);
export const MODULE_BY_KEY = MODULES.reduce((acc, m) => ({ ...acc, [m.key]: m }), {});

// The a-la-carte cost of every module, i.e. what "Everything" discounts from.
export const LIST_RATE = RATES.crmPlusErp + RATES.pm + RATES.hr + RATES.prod; // 1,099
export const EVERYTHING_DISCOUNT_PCT = Math.round((1 - RATES.everything / LIST_RATE) * 100); // 18

// ─── Engine ─────────────────────────────────────────────────────────────────

// Production cannot run without ERP Standard, so selecting it pulls ERP in.
// Order is normalised to MODULE_KEYS so breakdowns always read the same way.
export const normaliseModules = (keys) => {
  const set = new Set(keys);
  if (set.has('prod')) set.add('erp');
  return MODULE_KEYS.filter((k) => set.has(k));
};

// Rs. per user, per month before the Everything bundle is considered.
const alaCarteRate = (picked) => {
  const has = (k) => picked.includes(k);
  let rate = 0;
  if (has('crm') && has('erp')) rate = RATES.crmPlusErp;
  else if (has('crm')) rate = RATES.crmOnly;
  else if (has('erp')) rate = RATES.erpOnly;

  if (has('pm')) rate += RATES.pm;
  if (has('hr')) rate += RATES.hr;
  if (has('prod')) rate += RATES.prod;
  return rate;
};

// Rs. per user, per month for a set of modules. Taking all five is capped at the
// Everything rate, which is cheaper than the parts.
export const rateFor = (keys) => {
  const picked = normaliseModules(keys);
  if (MODULE_KEYS.every((k) => picked.includes(k))) return RATES.everything;
  return alaCarteRate(picked);
};

// What each module adds to the running total, so the estimator can show its
// working. Always a-la-carte: the Everything discount is a separate line, not
// something smuggled into whichever module happens to be last.
export const breakdownFor = (keys) => {
  const picked = normaliseModules(keys);
  let running = 0;
  return picked.map((key, i) => {
    const upto = alaCarteRate(picked.slice(0, i + 1));
    const amount = upto - running;
    running = upto;
    return { key, name: MODULE_BY_KEY[key].name, amount };
  });
};

/**
 * Full quote for a selection. All money is Rs., exclusive of GST.
 * `annual` is what actually leaves the bank in a year — 10 months on yearly
 * billing, 12 on monthly.
 */
export const quote = ({ modules = [], users = 1, aiSeats = 0, yearly = false }) => {
  const picked = normaliseModules(modules);
  const seats = Math.max(1, Math.round(users) || 1);
  const ai = Math.min(Math.max(0, Math.round(aiSeats) || 0), seats);

  const rate = rateFor(picked);
  const isEverything = picked.length === MODULE_KEYS.length;
  const bundleDiscount = isEverything ? LIST_RATE - RATES.everything : 0;

  const moduleMonthly = rate * seats;
  const aiMonthly = RATES.ai * ai;
  const monthly = moduleMonthly + aiMonthly;

  const annual = monthly * (yearly ? YEARLY_MONTHS_CHARGED : 12);
  const annualIfMonthly = monthly * 12;

  return {
    modules: picked,
    users: seats,
    aiSeats: ai,
    rate,
    isEverything,
    listRate: isEverything ? LIST_RATE : rate,
    bundleDiscount,
    breakdown: breakdownFor(picked),
    moduleMonthly,
    aiMonthly,
    monthly,
    annual,
    annualIfMonthly,
    annualSaving: annualIfMonthly - annual,
    needsQuote: seats > BULK_THRESHOLD,
    empty: picked.length === 0,
  };
};

// ─── Chips: nature of business ──────────────────────────────────────────────
// Each preset seats the estimator with a plausible team size and module mix.
export const BUSINESS_TYPES = [
  { key: 'sales', label: 'Sales team', icon: 'contact-round', users: 5, modules: ['crm'], blurb: 'Leads and follow-ups, nothing heavier.' },
  { key: 'agency', label: 'Marketing agency', icon: 'megaphone', users: 10, modules: ['crm', 'pm'], blurb: 'Campaigns to run, projects to deliver.' },
  { key: 'consultancy', label: 'Consultancy', icon: 'clipboard-list', users: 15, modules: ['crm', 'pm', 'hr'], blurb: 'Billable time, and the people behind it.' },
  { key: 'trading', label: 'Trading & distribution', icon: 'truck', users: 10, modules: ['crm', 'erp'], blurb: 'Stock in, invoices out, GST filed.' },
  { key: 'logistics', label: 'Logistics & delivery', icon: 'package-check', users: 10, modules: ['erp', 'hr'], blurb: 'Vehicles, freight bills and driver payroll.' },
  { key: 'retail', label: 'Retail', icon: 'store', users: 8, modules: ['erp', 'hr'], blurb: 'Raya POS at the counter, stock and staff rosters.' },
  { key: 'manufacturing', label: 'Manufacturing', icon: 'factory', users: 20, modules: MODULE_KEYS, blurb: 'Shop floor to balance sheet.' },
  { key: 'jewelry', label: 'Jewelry', icon: 'gem', users: 12, modules: ['crm', 'erp', 'prod'], blurb: 'Making, stock and customer orders.' },
  { key: 'healthcare', label: 'Healthcare', icon: 'stethoscope', users: 15, modules: ['crm', 'erp', 'hr'], blurb: 'Patients, supplies and staff.' },
];

export const DEFAULT_BUSINESS_TYPE = 'trading';

// ─── Worked examples (from the price sheets) ────────────────────────────────
// Totals are computed by the engine, never typed in, so they cannot drift.
export const WORKED_EXAMPLES = [
  { label: 'Sales team', detail: 'Raya CRM only', users: 5, modules: ['crm'] },
  { label: 'Trading company', detail: 'Raya ERP Standard only', users: 4, modules: ['erp'] },
  { label: 'Marketing agency', detail: 'Raya CRM + Project Management', users: 10, modules: ['crm', 'pm'] },
  { label: 'Distributor', detail: 'Raya CRM + Raya ERP Standard', users: 10, modules: ['crm', 'erp'] },
  { label: 'Consultancy', detail: 'Raya CRM + Project Management + HR', users: 15, modules: ['crm', 'pm', 'hr'] },
  { label: 'Manufacturing company', detail: 'Everything', users: 20, modules: MODULE_KEYS },
];

// ─── Small print ────────────────────────────────────────────────────────────
export const SMALL_PRINT = [
  { title: 'One company, one PAN', body: 'A subscription covers a single company — that is one PAN. Every GSTIN registered under that PAN is included, so multi-state branches cost nothing extra. A second PAN is a second subscription.', icon: 'landmark' },
  { title: 'Custom', body: `More than ${BULK_THRESHOLD} users? A bulk discount can be worked out. Reach out for your quote.`, icon: 'users' },
  { title: 'Setup', body: 'Raya CRM starts straight from the trial, with no onboarding fee. Raya ERP Standard onboarding is quoted per company, not per user.', icon: 'cog' },
  { title: 'Modules', body: 'Add-ons can be switched on or off any month. Raya ERP Standard comes whole, not in parts.', icon: 'layers' },
  { title: 'Taxes', body: 'Every rate on this page is exclusive of GST. Invoices are raised by Raya Solution & Company against your GSTIN.', icon: 'receipt-indian-rupee' },
];

export const PRICING_FAQS = [
  { q: 'Is the price per user or per company?', a: 'Both, in a sense: it is per user, per month, within one company. A subscription covers a single company — one PAN — and you pay for the people in it. There is no platform fee or company minimum on top.' },
  { q: 'What counts as one company?', a: 'One PAN. Every GSTIN registered under that PAN sits inside the same subscription, so a business trading from several states pays nothing extra for the additional registrations. A separate legal entity on its own PAN is a separate subscription, and group pricing can be worked out if you run several.' },
  { q: 'What do I pay if I take CRM and ERP together?', a: '₹599 per user, per month. It is the same number from either direction: Raya CRM at ₹299 plus ERP Standard at ₹300, or ERP Standard at ₹499 plus CRM & Marketing at ₹100.' },
  { q: 'Can I switch modules on and off?', a: 'Yes. Add-ons can be switched on any month and dropped the same way. Raya ERP Standard is the one exception — it is sold whole, not in parts.' },
  { q: 'How does yearly billing work?', a: 'Pay for the year at the start and you are billed for 10 months instead of 12. Two months free, add-ons included.' },
  { q: 'What does Raya AI actually do?', a: 'It does the work, not just the talking. Ask it to record a bill or an expense, log a sale, draft a quotation, find stock across warehouses or forecast demand, and it carries the action out against your live data — with every entry still going through your normal approvals and audit trail.' },
  { q: 'Do I have to buy Raya AI for everyone?', a: `No. Add it only to the owners or main accounts that need it, at ₹${RATES.ai} per seat, per month. Seats are yours to assign and remove from one month to the next.` },
  { q: 'Is there a setup fee?', a: 'Raya CRM starts straight from the trial with no onboarding fee. If you add Raya ERP Standard, onboarding is quoted once per company, not per user.' },
  { q: 'What about more than 30 users?', a: 'The rates on this page are list prices. Past 30 users a bulk discount can be worked out, so get in touch for a quote.' },
  { q: 'Are these prices inclusive of GST?', a: 'No. Every rate is exclusive of GST, which is added on the invoice at the applicable rate.' },
];

// ─── Structured data ────────────────────────────────────────────────────────
// Built from RATES so the price range Google sees is the range on the page.
// `detailed` adds a per-module Offer, which is only worth emitting on /pricing.
export const softwareApplicationSchema = ({ detailed = false } = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Raya ERP & CRM',
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'ERP, CRM',
  operatingSystem: 'Web, Android, iOS',
  installUrl: [PLAY_STORE_URL, APP_STORE_URL],
  url: 'https://erp.rayasolutions.in/',
  description: 'ERP and CRM software for growing Indian businesses — inventory, sales and invoicing, purchase, manufacturing, finance, projects, HR and CRM, with GST and e-Invoicing built in.',
  publisher: { '@type': 'Organization', name: 'Raya Software Solutions', url: 'https://erp.rayasolutions.in/' },
  featureList: [
    'Inventory Management', 'Sales & Invoicing', 'CRM & Contacts', 'Finance & Accounting',
    'Manufacturing & Operations', 'Projects', 'HR & Payroll', 'Reports & Analytics',
    'GST Compliance', 'Multi-warehouse Support', 'Point of Sale (POS) for retail counters', 'Raya AI assistant',
  ],
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'INR',
    // Cheapest way in is Raya CRM; the ceiling is Everything.
    lowPrice: RATES.crmOnly,
    highPrice: RATES.everything,
    offerCount: MODULES.length + 1,
    url: 'https://erp.rayasolutions.in/pricing',
    unitText: 'per user, per month',
    ...(detailed ? {
      offers: [
        ...MODULES.map((m) => ({
          '@type': 'Offer',
          name: m.name,
          description: m.tagline,
          price: m.base,
          priceCurrency: 'INR',
          availability: 'https://schema.org/InStock',
          url: 'https://erp.rayasolutions.in/pricing',
        })),
        {
          '@type': 'Offer',
          name: 'Everything',
          description: 'Raya CRM, Raya ERP Standard, Project Management, HR & Payroll and Production.',
          price: RATES.everything,
          priceCurrency: 'INR',
          availability: 'https://schema.org/InStock',
          url: 'https://erp.rayasolutions.in/pricing',
        },
      ],
    } : {}),
  },
});
